import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Swords } from 'lucide-react';

import { useMainButton } from '@/hooks/useMainButton';
import { useHaptics } from '@/hooks/useHaptics';
import { Loader } from '@/components/common/Loader';
import {
  useGetMyLinkQuery,
  usePlayerProfileQuery,
  useVerifyTokenMutation,
} from '@/services/qduelsApi';
import {
  classifyQduelsVerifyError,
  isNotLinkedError,
} from '@/services/qduelsErrors';
import { useUpsertProfileMutation } from '@/services/endpoints/games';
import { useLinkPlayerMutation } from '@/services/qbotApi';
import { useAppSelector } from '@/store/hooks';
import { cn } from '@/lib/cn';

// Clash Royale identifiers on the Laravel monolith (mirror the mobile-app).
const CR_GAME_ID = 6;
const CR_PLATFORM_ID = 5;

/**
 * "Collega account Clash Royale" — links the authenticated QLASH user to a
 * Clash Royale account through qduels' ownership verification.
 *
 * Supercell verifies ownership via a short-lived API token the user generates
 * INSIDE the game (Settings → API token for third-party verification). The
 * user pastes that token + their player tag (shown under their name); we send
 * both to `POST /v1/players/verify-token`, which checks them against Supercell
 * and links the account. The token is single-use / short-lived, so on failure
 * we tell the user to regenerate it in-game and retry.
 *
 * This is a distinct mechanism from the QBot `/cr/link` tag picker: qduels
 * keeps its OWN player link (proven ownership) because a duel match invite
 * requires a verified account, not just a tag lookup.
 *
 * Reached from Profile → "Account collegati" → Clash Royale, and from the
 * Sfida button in the friends list when the user isn't linked yet.
 */

// Same accepted charset as the QBot CR tag picker — Supercell tags use this
// base-14 alphabet. Accepts an optional leading '#'.
const CR_TAG_RE = /^[#]?[0289PYLQGRJCUV]{3,12}$/i;

/** Normalize a user-typed tag to the canonical `#XXXX` form qduels expects. */
function normalizeTag(raw: string): string {
  return `#${raw.trim().replace(/^#/, '').toUpperCase()}`;
}

/**
 * Dev-only: extract HTTP status + body from an RTK Query error so we can see
 * exactly why the Laravel tournament-profile upsert failed (regex/format vs
 * token vs auth). Appended to the warning only when import.meta.env.DEV.
 */
function formatUpsertError(err: unknown): string {
  if (!err || typeof err !== 'object') return String(err);
  const e = err as { status?: unknown; data?: unknown };
  const status = e.status != null ? String(e.status) : '?';
  const body = typeof e.data === 'string' ? e.data : JSON.stringify(e.data ?? {});
  return `${status} ${body}`.slice(0, 300);
}

export function DuelLink() {
  const { t } = useTranslation('duels');
  const navigate = useNavigate();
  const haptics = useHaptics();

  const linkQuery = useGetMyLinkQuery();
  const notLinked = isNotLinkedError(linkQuery.error);
  const linked = linkQuery.data ?? null;

  // Pull the linked account's CR name for display (best-effort). Skipped until
  // we actually have a linked tag.
  const profileQuery = usePlayerProfileQuery(linked?.playerTag ?? '', {
    skip: !linked?.playerTag,
  });

  const [verifyToken, { isLoading: verifying }] = useVerifyTokenMutation();
  // After proving ownership with qduels we ALSO register the account on the
  // Laravel monolith (so tournament join works) and prime the QBot CR section
  // (stats/decks). createProfile is the call the TMA was missing.
  const [upsertProfile] = useUpsertProfileMutation();
  const [linkQbot] = useLinkPlayerMutation();
  const user = useAppSelector((s) => s.auth.user);
  const [tag, setTag] = useState('');
  const [token, setToken] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [justLinked, setJustLinked] = useState(false);
  // Set when the qduels verify succeeded but the Laravel tournament-profile
  // registration failed — the account is linked for duels but tournament join
  // won't work until the user retries (regenerating the in-game token).
  const [tournamentWarning, setTournamentWarning] = useState<string | null>(null);
  // When the user taps "ricollega" on an already-linked account we reveal the
  // form again instead of navigating away.
  const [relinking, setRelinking] = useState(false);

  const showForm = notLinked || relinking;
  const valid = CR_TAG_RE.test(tag.trim()) && token.trim().length > 0;

  const submit = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!valid || verifying) return;
    setError(null);
    setTournamentWarning(null);
    const cleanTag = normalizeTag(tag); // "#TAG"
    const bareTag = cleanTag.replace('#', '');
    try {
      const res = await verifyToken({
        playerTag: cleanTag,
        token: token.trim(),
      }).unwrap();
      if (!res.verified) {
        // A 200 with verified:false would be unusual (the contract returns 422
        // for a bad token), but guard it so we never claim success wrongly.
        haptics.notification('error');
        setError(t('error.invalidToken'));
        return;
      }

      // Register the verified account on the Laravel monolith so CR tournament
      // join works. We send the TAG WITHOUT the in-game token: that token is
      // single-use and the qduels verify above already consumed it, so Laravel
      // re-validating it (#TAG-TOKEN path) would 403 "Invalid GameTag or api
      // token". The tag-only form makes Laravel validate by existence
      // (getProfile) — the exact check tournament join uses — and the stored
      // apiToken is unused by join anyway (marked "TODO da rimuovere" server-side).
      const nickname = user?.username || user?.firstName || bareTag;
      try {
        await upsertProfile({
          game_id: CR_GAME_ID,
          platform_id: CR_PLATFORM_ID,
          info: { id: cleanTag, nickname, meta: [] },
        }).unwrap();
      } catch (upsertErr) {
        // Account is linked for duels, but tournaments won't accept it yet.
        // In dev, surface the server's status + reason so we can pinpoint why.
        setTournamentWarning(
          import.meta.env.DEV
            ? `${t('error.tournaments')} [${formatUpsertError(upsertErr)}]`
            : t('error.tournaments'),
        );
      }

      // Prime the QBot CR stats/deck section so /cr resolves the tag right away
      // (best-effort: QBot also bridges to the Laravel profile on its own).
      try {
        await linkQbot({ tag: bareTag }).unwrap();
      } catch {
        /* ignore — QBot resolves the tag from the Laravel profile via its bridge */
      }

      haptics.notification('success');
      setJustLinked(true);
      setRelinking(false);
      setToken('');
    } catch (err) {
      haptics.notification('error');
      const kind = classifyQduelsVerifyError(err);
      setError(
        kind === 'invalid_token'
          ? t('error.invalidToken')
          : kind === 'already_linked'
            ? t('error.alreadyLinked')
            : kind === 'invalid_player_tag'
              ? t('error.invalidTag')
              : kind === 'auth'
                ? t('error.auth')
                : kind === 'network'
                  ? t('error.network')
                  : t('error.generic'),
      );
    }
  };

  // Telegram MainButton drives submission so the in-app keyboard's "Done" still
  // works alongside it. Only mounted while the form is visible.
  useMainButton({
    text: t('cta'),
    enabled: showForm && valid && !verifying,
    loading: verifying,
    onClick: () => void submit(),
  });

  // Clear the transient success banner if the user starts editing again.
  useEffect(() => {
    if (justLinked && relinking) setJustLinked(false);
  }, [justLinked, relinking]);

  return (
    <div className="flex flex-col gap-5 px-4 pb-6 pt-5">
      <header className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/15 text-brand">
            <Swords aria-hidden className="h-5 w-5" />
          </span>
          <h1 className="text-2xl font-bold text-fg">{t('title')}</h1>
        </div>
        <p className="text-sm text-fg-muted">{t('subtitle')}</p>
      </header>

      {/* Current link status */}
      {linkQuery.isLoading ? (
        <div className="flex h-20 items-center justify-center">
          <Loader />
        </div>
      ) : !showForm && linked ? (
        <section className="flex flex-col gap-3 rounded-xl bg-bg-secondary p-4">
          <div className="flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-success/15 text-success">
              <CheckCircle2 aria-hidden className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-fg">
                {profileQuery.data?.name ?? t('status.linkedFallback')}
              </p>
              <p className="truncate font-mono text-xs text-fg-muted">
                {linked.playerTag}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => {
              haptics.selection();
              setTag(linked.playerTag);
              setRelinking(true);
            }}
            className="self-start rounded-lg bg-bg-tertiary px-3 py-2 text-xs font-bold uppercase tracking-wide text-fg-muted transition-opacity active:opacity-80"
          >
            {t('status.relink')}
          </button>
        </section>
      ) : null}

      {/* Just-linked confirmation (after a successful verify, when not editing) */}
      {justLinked && !relinking && (
        <p
          className="flex items-center gap-2 rounded-xl bg-success/15 px-3 py-2 text-sm text-success"
          role="status"
        >
          <CheckCircle2 aria-hidden className="h-4 w-4 shrink-0" />
          {t('status.justLinked')}
        </p>
      )}

      {/* qduels link succeeded but the Laravel tournament-profile registration
          failed — tell the user tournaments need a retry (regenerate token). */}
      {tournamentWarning && (
        <p
          className="rounded-xl bg-warning/15 px-3 py-2 text-sm text-warning"
          role="alert"
        >
          {tournamentWarning}
        </p>
      )}

      {/* Link form */}
      {showForm && (
        <form onSubmit={submit} className="flex flex-col gap-4">
          {/* How-to instructions */}
          <ol className="flex flex-col gap-1.5 rounded-xl bg-bg-secondary p-4 text-xs text-fg-muted">
            <li>{t('howto.step1')}</li>
            <li>{t('howto.step2')}</li>
            <li>{t('howto.step3')}</li>
          </ol>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
              {t('field.tag.label')}
            </span>
            <input
              inputMode="text"
              autoCapitalize="characters"
              autoComplete="off"
              spellCheck={false}
              value={tag}
              onChange={(e) => {
                setError(null);
                setTag(e.target.value);
              }}
              placeholder="#2L9YV0QU"
              maxLength={13}
              className="rounded-lg border border-border bg-bg-secondary px-3 py-2 font-mono text-lg uppercase tracking-wider text-fg placeholder:text-fg-muted/60 focus:border-brand focus:outline-none"
            />
            <span className="text-[11px] text-fg-muted">{t('field.tag.hint')}</span>
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase tracking-wide text-fg-muted">
              {t('field.token.label')}
            </span>
            <input
              inputMode="text"
              autoComplete="off"
              spellCheck={false}
              value={token}
              onChange={(e) => {
                setError(null);
                setToken(e.target.value);
              }}
              placeholder={t('field.token.placeholder')}
              className="rounded-lg border border-border bg-bg-secondary px-3 py-2 font-mono text-sm text-fg placeholder:text-fg-muted/60 focus:border-brand focus:outline-none"
            />
            <span className="text-[11px] text-fg-muted">{t('field.token.hint')}</span>
          </label>

          {error && (
            <p
              className={cn(
                'rounded-md bg-danger/15 px-3 py-2 text-sm text-danger',
              )}
              role="alert"
            >
              {error}
            </p>
          )}

          {relinking && linked && (
            <button
              type="button"
              onClick={() => {
                haptics.selection();
                setRelinking(false);
                setError(null);
                navigate(-1);
              }}
              className="self-start text-xs font-semibold text-fg-muted underline"
            >
              {t('status.cancelRelink')}
            </button>
          )}

          {/* Primary CTA lives in <MainCta /> above the TabBar, driven by
              useMainButton above. Enter key still submits via the form. */}
        </form>
      )}
    </div>
  );
}
